import { Controller } from "@hotwired/stimulus"

// Global cache to prevent multiple requests for the same data
window.SimpleTypeaheadCache = window.SimpleTypeaheadCache || {}
window.SimpleTypeaheadPromises = window.SimpleTypeaheadPromises || {}

export default class extends Controller {
  static targets = ["input", "results"]
  static values = { 
    type: String,
    url: String,
    hiddenId: String,  // ID of hidden field to store selected item's ID
    callback: String   // Name of callback function to run on selection
  }
  
  connect() {
    this.cache = {} // Cache search results
    this.allData = null // Cache all data for prefetch
    this.setupTypeahead()
    this.prefetchData() // Prefetch all data on load
  }
  
  prefetchData() {
    const configs = {
      clients: {
        url: '/clients.json',
        displayKey: 'friendlyname'
      },
      properties: {
        url: '/properties.json',
        displayKey: 'streetaddress'
      },
      contacts: {
        url: '/contacts.json',
        displayKey: 'full_name'
      },
      galleries: {
        url: '/galleries.json',
        displayKey: 'name'
      },
      users: {
        url: '/users.json',
        displayKey: 'name'
      },
      articles: {
        url: '/articles.json',
        displayKey: 'title'
      },
      workflows: {
        url: '/workflows.json',
        displayKey: 'title'
      },
      taskLists: {
        url: '/task_lists.json',
        displayKey: 'name'
      }
    }
    
    const config = configs[this.typeValue] || {}
    const cacheKey = this.typeValue
    
    // Check if data is already cached
    if (window.SimpleTypeaheadCache[cacheKey]) {
      console.log(`[TYPEAHEAD] Using cached data for: ${cacheKey}`)
      this.allData = window.SimpleTypeaheadCache[cacheKey]
      this.displayKey = config.displayKey
      return
    }
    
    // Check if a request is already in progress
    if (window.SimpleTypeaheadPromises[cacheKey]) {
      console.log(`[TYPEAHEAD] Waiting for existing request: ${cacheKey}`)
      window.SimpleTypeaheadPromises[cacheKey].then(data => {
        this.allData = data
        this.displayKey = config.displayKey
      }).catch(() => {})
      return
    }
    
    // Make the request and cache the promise
    console.log(`[TYPEAHEAD] Making new request for: ${cacheKey} to ${config.url}`)
    window.SimpleTypeaheadPromises[cacheKey] = fetch(config.url, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      // Cache the data globally
      window.SimpleTypeaheadCache[cacheKey] = data
      this.allData = data
      this.displayKey = config.displayKey
      return data
    })
    .catch(error => {
      // Try without .json extension as fallback
      return fetch(config.url.replace('.json', ''), {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.json())
      .then(data => {
        window.SimpleTypeaheadCache[cacheKey] = data
        this.allData = data
        this.displayKey = config.displayKey
        return data
      })
    })
    .finally(() => {
      // Clean up the promise from the cache
      delete window.SimpleTypeaheadPromises[cacheKey]
    })
  }
  
  setupTypeahead() {
    // Create dropdown container
    this.dropdown = document.createElement('div')
    this.dropdown.className = 'typeahead-dropdown'
    this.dropdown.style.display = 'none'
    this.dropdown.style.position = 'absolute'
    this.dropdown.style.zIndex = '1000'
    this.dropdown.style.backgroundColor = 'white'
    this.dropdown.style.border = '1px solid #ccc'
    this.dropdown.style.borderRadius = '4px'
    this.dropdown.style.maxHeight = '200px'
    this.dropdown.style.overflowY = 'auto'
    this.dropdown.style.width = '100%'
    
    // Position dropdown below input
    this.element.style.position = 'relative'
    this.element.appendChild(this.dropdown)
    
    // Setup event listeners
    const input = this.element.querySelector('input')
    this.input = input
    
    // Immediate search from cached data
    input.addEventListener('input', (e) => {
      const query = e.target.value
      
      if (query.length < 1) {
        this.hideDropdown()
        return
      }
      
      this.search(query)
    })
    
    // Hide on blur and clear hidden ID if field is empty
    input.addEventListener('blur', () => {
      setTimeout(() => {
        this.hideDropdown()
        // Clear hidden ID field if main field is empty
        if (!this.input.value && this.hiddenIdValue) {
          const hiddenField = document.getElementById(this.hiddenIdValue)
          if (hiddenField) {
            hiddenField.value = ''
          }
        }
      }, 200)
    })
    
    // Handle keyboard navigation - use capture phase to run before other event listeners
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
        this.handleKeyboard(e)
      }
    }, true) // true = capture phase (runs first)
  }
  
  search(query) {
    // Use prefetched data if available
    if (this.allData) {
      const lowerQuery = query.toLowerCase()
      const filtered = this.allData.filter(item => {
        const value = item[this.displayKey] || item.name || ''
        return value.toLowerCase().includes(lowerQuery)
      })
      
      // Limit to top 10 results for performance
      const limited = filtered.slice(0, 10)
      this.showResults(limited, this.displayKey)
      return
    }
    
    // Fallback to server search if no prefetched data
    const configs = {
      clients: {
        url: '/clients.json',
        displayKey: 'friendlyname'
      },
      properties: {
        url: '/properties.json',
        displayKey: 'streetaddress'
      },
      contacts: {
        url: '/contacts.json',
        displayKey: 'full_name'
      }
    }
    
    const config = configs[this.typeValue] || {}
    const url = config.url + '?q=' + encodeURIComponent(query)
    
    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.showResults(data, config.displayKey)
    })
    .catch(error => {})
  }
  
  showResults(items, displayKey) {
    if (!items || items.length === 0) {
      this.hideDropdown()
      return
    }
    
    this.dropdown.innerHTML = ''
    this.currentIndex = -1
    this.lastResults = items // Store for keyboard selection
    
    items.forEach((item, index) => {
      const div = document.createElement('div')
      div.className = 'typeahead-item'
      div.style.padding = '8px 12px'
      div.style.cursor = 'pointer'
      div.textContent = item[displayKey] || item.name || ''
      div.dataset.value = item[displayKey] || item.name || ''
      div.dataset.index = index
      
      div.addEventListener('mouseenter', () => {
        this.highlightItem(index)
      })
      
      div.addEventListener('click', () => {
        this.selectItem(item[displayKey] || item.name || '', item)
      })
      
      this.dropdown.appendChild(div)
    })
    
    this.dropdown.style.display = 'block'
    
    // Don't auto-highlight - let user explicitly navigate to select from typeahead
  }
  
  highlightItem(index) {
    const items = this.dropdown.querySelectorAll('.typeahead-item')
    items.forEach((item, i) => {
      if (i === index) {
        item.style.backgroundColor = '#f0f0f0'
      } else {
        item.style.backgroundColor = 'white'
      }
    })
    this.currentIndex = index
  }
  
  selectItem(value, item = null) {
    this.input.value = value
    this.hideDropdown()
    
    // If we have an item with an ID and a hidden field is configured, set it
    if (item && item.id && this.hiddenIdValue) {
      const hiddenField = document.getElementById(this.hiddenIdValue)
      if (hiddenField) {
        hiddenField.value = item.id
      }
    }
    
    // Call callback if configured (for transaction contact fields)
    if (item && this.callbackValue && window[this.callbackValue]) {
      window[this.callbackValue](item, this.extractContactType())
    }
    
    // Trigger change event
    const event = new Event('change', { bubbles: true })
    this.input.dispatchEvent(event)
  }
  
  extractContactType() {
    // Extract contact type from the field ID (e.g., "transaction_title_full_name" -> "title")
    const match = this.input.id.match(/transaction_(.+)_full_name/)
    return match ? match[1] : null
  }
  
  hideDropdown() {
    this.dropdown.style.display = 'none'
    this.currentIndex = -1
  }
  
  handleKeyboard(e) {
    const items = this.dropdown.querySelectorAll('.typeahead-item')
    
    if (e.key === 'ArrowDown') {
      if (items.length === 0) return
      e.preventDefault()
      // Start from -1 if no item selected, otherwise increment
      this.currentIndex = this.currentIndex < 0 ? 0 : Math.min(this.currentIndex + 1, items.length - 1)
      this.highlightItem(this.currentIndex)
    } else if (e.key === 'ArrowUp') {
      if (items.length === 0) return
      e.preventDefault()
      this.currentIndex = Math.max(this.currentIndex - 1, -1)
      if (this.currentIndex >= 0) {
        this.highlightItem(this.currentIndex)
      } else {
        // Clear all highlighting when going back to -1
        const items = this.dropdown.querySelectorAll('.typeahead-item')
        items.forEach(item => item.style.backgroundColor = 'white')
      }
    } else if (e.key === 'Enter') {
      
      // Only intercept Enter if user has explicitly highlighted an item with arrow keys
      if (this.currentIndex >= 0 && items.length > 0) {
        const selectedItem = items[this.currentIndex]
        if (selectedItem) {
          e.preventDefault()
          const itemData = this.lastResults ? this.lastResults[this.currentIndex] : null
          this.selectItem(selectedItem.dataset.value, itemData)
          
          // After selecting from typeahead, also submit the form
          const form = this.input.closest('form')
          if (form) {
            form.submit()
          }
          return
        }
      }
      
      // For all other cases, hide dropdown and allow normal form submission
      this.hideDropdown()
      
      // Force form submission by finding and submitting the form
      const form = this.input.closest('form')
      if (form) {
        form.submit()
        e.preventDefault() // Prevent any other handlers
        e.stopPropagation() // Stop the old typeahead from interfering
      }
      // Don't prevent default - let the form submit with whatever the user typed
    } else if (e.key === 'Tab') {
      // For Tab, select highlighted item if available, then continue with tab navigation
      if (items.length > 0) {
        if (this.currentIndex === -1 && items.length > 0) {
          this.currentIndex = 0
        }
        
        if (this.currentIndex >= 0) {
          const selectedItem = items[this.currentIndex]
          if (selectedItem) {
            e.preventDefault()
            const itemData = this.lastResults ? this.lastResults[this.currentIndex] : null
            this.selectItem(selectedItem.dataset.value, itemData)
            
            // Allow the browser to handle tab to next field after a brief delay
            setTimeout(() => {
              // Find the next focusable element
              const focusableElements = 'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
              const focusables = Array.from(document.querySelectorAll(focusableElements))
              const currentIndex = focusables.indexOf(this.input)
              if (currentIndex > -1 && currentIndex < focusables.length - 1) {
                focusables[currentIndex + 1].focus()
              }
            }, 50)
          }
        }
      }
    }
  }
  
  disconnect() {
    if (this.dropdown) {
      this.dropdown.remove()
    }
  }
}